import { Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { LoginType } from 'api-types/models/LoginDto';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectAuthIsLoading, selectAuthUser } from 'app/authSlice';
import useAmplifyAuth from 'hooks/useAmplifyAuth';
import useUserSettings from 'hooks/useUserSettings';
import IRoute from 'interfaces/route';
import { selectAllowedRoutes } from 'app/selectors';
import MenuItemsLoader from 'components/loading/menuLoader';
import { loginRequest } from 'authConfig';
import { renewSSOTokens } from 'utils/renewSSOTokens';
import { getUserGroups, isBrunchLoginType } from 'utils/user';
import { setConfigThunk } from 'app/configSlice';
import { AuthenticatedLayout } from '../layout/AuthenticatedLayout';

interface ProtectedRouteProps {
    route: IRoute;
}

export function ProtectedRoute({
    route
}: Readonly<ProtectedRouteProps>): React.ReactNode {
    const loginType = localStorage.getItem('login');
    const { instance, accounts, inProgress } = useMsal();
    const dispatch = useAppDispatch();

    useAmplifyAuth();

    const user = useAppSelector(selectAuthUser);
    const isAuthLoading = useAppSelector(selectAuthIsLoading);
    const isAuthenticated = useIsAuthenticated();
    const allowedRoutes = useAppSelector(selectAllowedRoutes);
    const { data: userSettings, isLoading: isUserSettingsLoading } =
        useUserSettings();

    useEffect(() => {
        if (
            accounts.length &&
            accounts[0].idTokenClaims?.exp &&
            accounts[0].idTokenClaims.exp < Date.now() / 1000 &&
            inProgress === InteractionStatus.None
        ) {
            renewSSOTokens(instance, accounts[0], loginRequest).then(
                (token) => {
                    if (token) {
                        dispatch(
                            setConfigThunk({
                                idToken: token.idToken,
                                accessToken: token.accessToken
                            })
                        );
                    }
                }
            );
        }
    }, [accounts, instance, inProgress, dispatch]);

    const msalBlocksRoute =
        inProgress === InteractionStatus.Startup ||
        inProgress === InteractionStatus.HandleRedirect ||
        inProgress === InteractionStatus.AcquireToken;

    if (msalBlocksRoute) {
        return <MenuItemsLoader />;
    }

    if (
        (loginType === LoginType.SPORTRADAR && !isAuthenticated) ||
        ((loginType === LoginType.BRUNCH || loginType === LoginType.COACH) &&
            user === null &&
            !isAuthLoading) ||
        loginType === null
    ) {
        return <Navigate to='/login' />;
    }

    if (loginType !== LoginType.SPORTRADAR && !user) {
        return <MenuItemsLoader />;
    }

    if (isUserSettingsLoading && loginType === LoginType.SPORTRADAR) {
        return <MenuItemsLoader />;
    }

    const userGroups = getUserGroups(user);
    const canAccessBrunchOnlyRoutes =
        userGroups.includes(LoginType.BRUNCH) ||
        (loginType === LoginType.BRUNCH && userGroups.length === 0);

    if (route.brunchOnly === true && !canAccessBrunchOnlyRoutes) {
        if (!isBrunchLoginType(loginType)) {
            return <Navigate to='/login' />;
        }

        const fallbackRoute = allowedRoutes[0] ?? '/booking';
        return <Navigate to={fallbackRoute} />;
    }

    const routePathForAccess = route.path === '/:date' ? '/' : route.path;

    if (
        !allowedRoutes.includes(routePathForAccess) &&
        !(route.path === '/users' && userSettings?.officeManagement)
    ) {
        const fallbackRoute = allowedRoutes[0] ?? '/';
        return <Navigate to={fallbackRoute} />;
    }

    return <AuthenticatedLayout route={route} />;
}
